// Importaciones
import { useState, useEffect, useContext, useCallback } from "react";
import { useForm } from "react-hook-form";
import { SaveAction, CardHeaderWidget, BreadCrumbs } from "../../../components";
import {
  Card,
  CardContent,
  Snackbar,
  Alert,
  Dialog,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
} from "@mui/material";
import { AxiosContext } from "../../../context/AxiosContext";
import { makeStyles } from "@mui/styles";
import viewsTexts from "../../../common/viewsTexts.json";
import { alphabeticalSort } from "../../../helpers";
import TextFieldWidgetText2 from "components/TextFieldWidget/TextFieldWidgetText2";
import SelectMultipleWidgetFilter from "components/SelectMultipleWidgetFilter";

const VarietyAdd = (props) => {
  // Contextos
  const axiosContext = useContext(AxiosContext);

  // Estados
  const [loadingQueryState, setLoadingQueryState] = useState(false);
  const [successQueryState, setSuccessQueryState] = useState(false);
  const [specieDataState, setSpecieDataState] = useState([]);
  const [errorState, setErrorState] = useState(false);
  const [errorState2, setErrorState2] = useState(false);
  const [errorState3, setErrorState3] = useState(false);
  const [loadingClientDataState, setLoadingClientDataState] = useState(false);
  const [openDialogState, setOpenDialogState] = useState(false);
  const [pendingValues, setPendingValues] = useState(null);

  // useForm
  const {
    handleSubmit,
    control,
    watch,
    reset,
    getValues,
    formState: { errors },
  } = useForm();

  // Variedades (campos dinámicos)
  const varieties = Array.from({ length: 10 }, (_, i) => watch(`variety${i}`));

  // Funciones principales
  // Submit: abre el diálogo de confirmación
  const onSubmit = async (values) => {
    // Validación: al menos una variedad debe estar ingresada
    const isAnyVarietyFilled = varieties.some(
      (value) => value && value.trim() !== ""
    );
    if (!isAnyVarietyFilled) {
      setErrorState3(true);
      return;
    }
    // Validación: no permitir variedades repetidas
    const filledVarieties = varieties.filter((v) => v && v.trim() !== "");
    const hasDuplicates =
      new Set(filledVarieties).size !== filledVarieties.length;
    if (hasDuplicates) {
      setErrorState2(true);
      return;
    }
    setPendingValues(values);
    setOpenDialogState(true);
  };

  // Confirmar en el diálogo y guardar
  const handleConfirmDialog = async () => {
    setOpenDialogState(false);
    if (!pendingValues) return;
    setLoadingQueryState(true);
    const values = pendingValues;
    const varietiesValues = [];
    for (let i = 0; i < 5; i++) {
      const fieldName = `variety${i}`;
      if (values[fieldName]) {
        varietiesValues.push(values[fieldName]);
      }
    }
    const body = {
      label: varietiesValues,
      specie: values.specie?.label,
      specieValue: values.specie?.value,
    };
    try {
      const response = await axiosContext.authAxios.post("v1/variety", body);
      if (response.status === 200) {
        setSuccessQueryState(true);
        reset({ variety: "", specie: "" });
      }
    } catch (err) {
      if (err.request.status === 500) {
        setErrorState(true);
      }
    } finally {
      setLoadingQueryState(false);
      setPendingValues(null);
    }
  };

  // Funciones auxiliares para Snackbars
  const handleCloseSnackBarsComponent = () => setErrorState(false);
  const handleCloseSnackBarsComponent2 = () => setErrorState2(false);
  const handleCloseSnackBarsComponent3 = () => setErrorState3(false);
  const onCloseSnack = () => setSuccessQueryState(false);

  // useEffect: obtener especies
  useEffect(() => {
    getSpecieAsync();
  }, []);

  // Función para obtener especies
  const getSpecieAsync = useCallback(async () => {
    setLoadingClientDataState(true);
    try {
      let response = await axiosContext.authAxios.get("/v1/specie");
      const { data, status } = response;
      if (status === 200) {
        setSpecieDataState(alphabeticalSort(data, "label"));
      }
    } catch (error) {
      console.log(error);
    } finally {
      setLoadingClientDataState(false);
    }
  }, [axiosContext, setLoadingClientDataState, setSpecieDataState]);

  // Render
  return (
    <div className="container">
      <Card elevation={0}>
        <CardHeaderWidget
          title={viewsTexts.titles.variety.varietyAdd.title}
          subheader={<BreadCrumbs subTitle="" />}
        />
        <CardContent>
          <div className="container">
            {/* Diálogo de confirmación */}
            <Dialog
              open={openDialogState}
              onClose={() => setOpenDialogState(false)}
              aria-labelledby="alert-dialog-title"
              aria-describedby="alert-dialog-description"
              sx={{
                "& .MuiDialog-paper": {
                  width: "100%",
                  maxWidth: "500px",
                  borderRadius: "10px",
                },
              }}
            >
              <DialogContent className="p-6">
                <DialogContentText
                  id="alert-dialog-description"
                  className="font-normal leading-normal"
                >
                  Una vez ingresadas las variedades, deberá contactar a TI para
                  eliminarlas,{" "}
                  <span className="font-semibold">¿desea continuar?</span>
                </DialogContentText>
              </DialogContent>
              <DialogActions className="p-6 gap-6">
                <Button
                  onClick={() => setOpenDialogState(false)}
                  color="primary"
                >
                  Cancelar
                </Button>
                <Button
                  onClick={handleConfirmDialog}
                  color="secondary"
                  variant="contained"
                  autoFocus
                >
                  Continuar
                </Button>
              </DialogActions>
            </Dialog>
            {/* Formulario de variedades */}
            <form autoComplete="on" onSubmit={handleSubmit(onSubmit)}>
              <SelectMultipleWidgetFilter
                errors={errors.specie}
                loading={loadingClientDataState}
                control={control}
                labeltext="Specie"
                name="specie"
                placeholder="Seleccione la Especie"
                display="vertical"
                variant="outlined"
                minheight={false}
                isClearable={true}
                isSearchable
                req={true}
                defaultValue=""
                options={specieDataState}
                sizeSelect={"large"}
                isMulti={false}
              />
              <div className="grid gap-x-8 gap-y-4 mt-6 lg:grid-cols-2">
                {[...Array(6)].map((_, i) => (
                  <div key={i}>
                    <TextFieldWidgetText2
                      InputLabelProps={{ shrink: true }}
                      name={`variety${i}`}
                      labeltext={`variedad ${i + 1}`}
                      // req={i === 0}
                      control={control}
                      errors={errors[`variety${i}`]}
                      defaultValue=""
                      disabled={false}
                      display="vertical"
                    />
                  </div>
                ))}
              </div>
              <div className="flex justify-center mt-6">
                <SaveAction
                  title="Guardar"
                  color="secondary"
                  size="large"
                  className="roundedButton ml-md-2"
                  variant="contained"
                  disabled={loadingQueryState}
                  messageType="add"
                  mutationLoading={loadingQueryState}
                  success={successQueryState}
                  actionClose={onCloseSnack}
                />
              </div>
            </form>
          </div>
          {/* Snackbars de error y éxito */}
          {errorState && (
            <Snackbar
              anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
              open={errorState}
              onClose={handleCloseSnackBarsComponent}
              autoHideDuration={4500}
              key="error-snackbar"
            >
              <Alert
                onClose={handleCloseSnackBarsComponent}
                severity="error"
                variant="filled"
                sx={{ width: "100%" }}
              >
                El nombre de agrícola ya existe
              </Alert>
            </Snackbar>
          )}
          {errorState2 && (
            <Snackbar
              anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
              open={errorState2}
              onClose={handleCloseSnackBarsComponent2}
              autoHideDuration={4500}
              key="error-snackbar2"
            >
              <Alert
                onClose={handleCloseSnackBarsComponent2}
                severity="error"
                variant="filled"
                sx={{ width: "100%" }}
              >
                Hay al menos una agrícola repetida
              </Alert>
            </Snackbar>
          )}
          {errorState3 && (
            <Snackbar
              anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
              open={errorState3}
              onClose={handleCloseSnackBarsComponent3}
              autoHideDuration={4500}
              key="error-snackbar3"
            >
              <Alert
                onClose={handleCloseSnackBarsComponent3}
                severity="error"
                variant="filled"
                sx={{ width: "100%" }}
              >
                Debe ingresar al menos una variedad
              </Alert>
            </Snackbar>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default VarietyAdd;
